import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent
} from 'react';
import * as d3 from 'd3';
import { Card, Dropdown } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toPng } from 'html-to-image';
import { getColor, toPersianDigit } from '@/helpers/utils';

const width = 460;
const height = 400;
const LABEL_FONT = 'dana, sans-serif';

type Rgb = [number, number, number];

type AnchorKind = 'empty' | 'brand' | 'url' | 'text';

type AnchorBubbleDataItem = {
  name?: string;
  value: number;
  color?: string;
};

type AnchorBubbleNode = {
  name: string;
  raw: string;
  kind: AnchorKind;
  v: number;
  share: number;
  color: string;
  x?: number;
  y?: number;
};

type AnchorBubbleChartData =
  | AnchorBubbleDataItem[]
  | { total?: number; items?: AnchorBubbleDataItem[] };

type AnchorBubbleChartProps = {
  data?: AnchorBubbleChartData;
  /** Target host from the route, used to mark brand anchors. */
  domain?: string | null;
  /** Raw anchor currently filtering the table. Empty string means blank anchors. */
  selectedRaw?: string | null;
  onSelect?: (raw: string) => void;
};

function readChartData(data?: AnchorBubbleChartData): {
  items: AnchorBubbleDataItem[];
  total?: number;
} {
  if (Array.isArray(data)) return { items: data };
  return { items: data?.items ?? [], total: data?.total };
}

const KIND_LABELS: { kind: AnchorKind; label: string }[] = [
  { kind: 'brand', label: 'نام برند' },
  { kind: 'url', label: 'آدرس سایت' },
  { kind: 'text', label: 'متن معمولی' },
  { kind: 'empty', label: 'بدون متن' }
];

/**
 * Swatches from the appearance drawer, skipping the first (default beige).
 * Order matches the drawer: teal, green, blue, purple.
 */
const KIND_COLORS: Record<AnchorKind, string> = {
  brand: '#02928b',
  url: '#10b981',
  text: '#3b82f6',
  empty: '#a855f7'
};

function brandToken(domain?: string | null): string {
  const host = String(domain ?? '')
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .split('/')[0]
    .split(':')[0]
    .toLowerCase();
  return host.split('.')[0] ?? '';
}

function isUrlAnchor(text: string): boolean {
  const value = text.trim();
  if (/^https?:\/\//i.test(value) || /^www\./i.test(value)) return true;
  return (
    !/\s/.test(value) &&
    /^[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i.test(value)
  );
}

function classifyAnchor(
  raw: string,
  display: string,
  domain?: string | null
): AnchorKind {
  if (!raw.trim()) return 'empty';
  if (isUrlAnchor(display)) return 'url';
  const token = brandToken(domain);
  if (token.length >= 2 && display.toLowerCase().includes(token)) return 'brand';
  return 'text';
}

function labelColor(fill: string): string {
  const rgb = parseCssColor(fill);
  if (!rgb) return '#ffffff';
  return relativeLuminance(rgb) > 0.62 ? '#1e293b' : '#ffffff';
}

/**
 * Text sits inside the inscribed square of the disc (padding is 0.36r) and line-clamps,
 * so a long anchor cannot paint past the circle. Full text stays on the tooltip.
 */
function bubbleLabelLayout(radius: number): {
  fontPx: number;
  lineClamp: number;
  padding: number;
} {
  return {
    fontPx: Math.max(8, Math.min(13, radius * 0.2)),
    lineClamp: radius >= 36 ? 3 : radius >= 22 ? 2 : 1,
    padding: radius * 0.36
  };
}

function parseCssColor(input: string): Rgb | null {
  const value = input.trim();
  const hex = value.replace('#', '');
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) {
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map(char => char + char)
            .join('')
        : hex;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16)
    ];
  }
  const rgb = value.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (!rgb) return null;
  return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
}

function relativeLuminance([red, green, blue]: Rgb): number {
  const channel = (value: number) => {
    const scaled = value / 255;
    return scaled <= 0.03928
      ? scaled / 12.92
      : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)
  );
}

function tooltipLabel(node: AnchorBubbleNode): string {
  return toPersianDigit(`${node.name}: ${node.v} (${node.share}٪)`);
}

function isSelectedAnchor(node: AnchorBubbleNode, selectedRaw?: string | null) {
  if (selectedRaw == null) return false;
  if (!node.raw.trim()) return selectedRaw === '';
  return selectedRaw === node.name;
}

const ForceGraph = ({
  nodes,
  selectedRaw,
  onSelect
}: {
  nodes: AnchorBubbleNode[];
  selectedRaw?: string | null;
  onSelect?: (raw: string) => void;
}) => {
    const [animatedNodes, setAnimatedNodes] = useState<AnchorBubbleNode[]>([]);

    const radiusScale = (value: number) => {
      const fx = d3
        .scaleSqrt()
        .range([18, 58])
        .domain([
          0.95 * (d3.min(nodes, (item: AnchorBubbleNode) => item.v) ?? 0),
          1.05 * (d3.max(nodes, (item: AnchorBubbleNode) => item.v) ?? 1)
        ]);
      return fx(value);
    };

    const showTooltip = function (
      e: ReactMouseEvent<SVGGElement>,
      item: AnchorBubbleNode
    ) {
      e.currentTarget.classList.add('opacity-75');
      d3.select('.d3-tooltip').transition().duration(200);
      d3.select('.d3-tooltip')
        .style('opacity', 1)
        .style('border', `1px solid ${item.color}`)
        .text(tooltipLabel(item))
        .style('left', e.clientX - 40 + 'px')
        .style('top', e.clientY - 40 + 'px');
    };

    const moveTooltip = function (e: ReactMouseEvent<SVGGElement>) {
      e.currentTarget.classList.add('opacity-75');
      d3.select('.d3-tooltip')
        .style('opacity', 1)
        .style('left', e.clientX - 40 + 'px')
        .style('top', e.clientY - 40 + 'px');
    };

    const hideTooltip = function (e: ReactMouseEvent<SVGGElement>) {
      e.currentTarget.classList.remove('opacity-75');
      d3.select('.d3-tooltip').transition().duration(200).style('opacity', 0);
    };

    useEffect(() => {
      const simulation = d3
        .forceSimulation<AnchorBubbleNode>(nodes)
        .velocityDecay(0.7)
        .force('x', d3.forceX().strength(0.05))
        .force('y', d3.forceY().strength(0.05))
        .force(
          'collide',
          d3.forceCollide<AnchorBubbleNode>(d => radiusScale(d.v) + 4)
        );

      simulation.on('tick', () => {
        setAnimatedNodes([...simulation.nodes()]);
      });

      simulation.alpha(0.01).restart();

      return () => {
        simulation.stop();
      };
    }, [nodes]);

    return (
      <>
        {animatedNodes.map((node, index) => {
          const radius = radiusScale(node.v);
          const selected = isSelectedAnchor(node, selectedRaw);
          const label = bubbleLabelLayout(radius);
          const labelStyle: CSSProperties = {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: label.padding,
            color: labelColor(node.color),
            fontFamily: LABEL_FONT,
            fontSize: label.fontPx,
            lineHeight: 1.25,
            textAlign: 'center'
          };
          const clampStyle: CSSProperties = {
            width: '100%',
            minWidth: 0,
            display: '-webkit-box',
            WebkitLineClamp: label.lineClamp,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            overflowWrap: 'anywhere'
          };
          return (
            <g
              key={`${node.name}-${index}`}
              transform={`translate(${width / 2 + (node.x ?? 0)}, ${
                height / 2 + (node.y ?? 0)
              })`}
              style={{ cursor: 'pointer' }}
              onMouseOver={e => showTooltip(e, node)}
              onMouseMove={e => moveTooltip(e)}
              onMouseLeave={e => hideTooltip(e)}
              onClick={() => onSelect?.(node.raw.trim() ? node.name : '')}
            >
              <circle
                r={radius}
                fill={node.color}
                stroke={selected ? getColor('gray-1100') : 'transparent'}
                strokeWidth={selected ? 3 : 0}
              />
              <foreignObject
                x={-radius}
                y={-radius}
                width={radius * 2}
                height={radius * 2}
                pointerEvents="none"
              >
                <div
                  {...({
                    xmlns: 'http://www.w3.org/1999/xhtml',
                    style: labelStyle
                  } as HTMLAttributes<HTMLDivElement>)}
                >
                  <span dir="auto" style={clampStyle}>
                    {node.name}
                  </span>
                </div>
              </foreignObject>
            </g>
          );
        })}
      </>
    );
};

const AnchorBubbleChart = ({
  data,
  domain,
  selectedRaw,
  onSelect
}: AnchorBubbleChartProps) => {
  const nodes = useMemo(() => {
    const chart = readChartData(data);
    const rows = chart.items;
    const plotted = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const total = chart.total && chart.total > 0 ? chart.total : plotted;
    return rows.map(item => {
      const raw = item.name ?? '';
      const name = raw.trim() ? safeDecode(raw) : '[بدون متن پیوند]';
      const kind = classifyAnchor(raw, name, domain);
      const share = total ? Math.round((Number(item.value) * 100) / total) : 0;
      return {
        name,
        raw,
        kind,
        v: Number(item.value),
        share,
        color: KIND_COLORS[kind]
      };
    });
  }, [data, domain]);
  const cardRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const savePng = async () => {
    const node = exportRef.current;
    if (!node) return;
    const backgroundColor = getColor('card-bg') || '#fff';
    const dataUrl = await toPng(node, {
      backgroundColor,
      pixelRatio: 2,
      cacheBust: true
    });
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = 'anchor-text-mizfa-tools.png';
    link.click();
  };

  return (
    <Card className="my-2" ref={cardRef}>
      <FalconCardHeader
        title={
          <div>
            متن پیوند <HelpTooltip html="hello world 213123" />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <Dropdown
            align="end"
            className="btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                onClick={() => {
                  void savePng();
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 py-1"
              >
                ذخیره تصویر
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  cardRef.current?.requestFullscreen();
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 py-1"
              >
                تمام صفحه
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
      <Card.Body className="py-2 my-2 d-flex flex-column position-relative">
        <div ref={exportRef} className="d-flex align-items-center gap-3">
          <div className="d-flex flex-column gap-2 flex-shrink-0">
            {KIND_LABELS.map(item => (
              <span
                key={item.kind}
                className="d-inline-flex align-items-center gap-2 fs--1 text-700"
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: KIND_COLORS[item.kind],
                    display: 'inline-block',
                    flexShrink: 0
                  }}
                />
                {item.label}
              </span>
            ))}
          </div>
          <div className="position-relative flex-grow-1" style={{ height: '28rem', minWidth: 0 }}>
            <div className="d3-tooltip"></div>
            <svg
              className="packed-bubble-svg h-100 w-100"
              viewBox={'0 0 ' + width + ' ' + height}
            >
              <ForceGraph
                nodes={nodes}
                selectedRaw={selectedRaw}
                onSelect={onSelect}
              />
            </svg>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default memo(AnchorBubbleChart);
